/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Card,
  Tabs,
  Spin,
  Button,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';
import { checkToken } from 'api/utils';
import { onError } from 'utils/handlers';
import { formatReceived, formatReceivedValues } from 'utils/formatValues';
import { areaBuscar, normalizeData } from 'utils/normalizers';
import ModalPrint from 'components/ModalPrint';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectUser } from 'store/auth';

import { descargarComprobanteSupervivencia, descargarComprobanteSupervivenciaUsuarioNoEmp } from 'api/nominas/consultaComprobanteDeSupervivencia';
import { getEmpleadosConDatosOpFilteredPaginated, getEmpleadosFiltered } from 'api/estructuraOrganizacional/empleados';
import { getPuestos } from 'api/catalogos/puestos';
import { getAreas } from 'api/catalogos';
import { getAdministracionDePlazas } from 'api/catalogos/admnistracionDePlazas';
import { getPlazas } from 'api/catalogos/plazas';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';

import {
  getHistorialComprobantesDeSupervivencia,
  postHistorialComprobanteDeSupervivencia,
} from 'api/nominas/historialComprobanteDeSupervivencia';
import {
  getFirmantesComprobantesDeSupervivencia,
  postFirmanteComprobantesDeSupervivencia,
} from 'api/nominas/firmanteComprobanteDeSupervivencia';
import {
  TIPO_DE_FUNCION_JUBILADO,
  TIPO_DE_FUNCION_PENSIONADO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import namePDF from 'components/PDF/namePDF';
import CustomSearch from 'components/customSearch';
import { getDatoOperacionalesDeEmpleados } from 'api/estructuraOrganizacional/datosOperacionalesDeEmpleados';
import HistorialComprobanteDeSupervivencia from './HistorialComprobanteDeSupervivencia';
import FirmanteComprobanteDeSupervivencia from './FirmanteComprobanteDeSupervivencia';

const { TabPane } = Tabs;

/* eslint-disable react/prop-types */
const ConsultaComprobanteDeSupervivencia = ({ permission }) => {
  const [form] = Form.useForm();
  const [currentTabKey, setCurrentTabKey] = useState('comprobante');
  const [loading, setLoading] = useState();
  const [empleados, setEmpleados] = useState([]);
  const [empleadosFirmate, setEmpleadosFirmante] = useState([]);
  const [areas, setAreas] = useState();
  const [puestos, setPuestos] = useState();
  const [allowPrint, setAllowPrint] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [adminPlazas, setAdminPlazas] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const user = useSelector(selectUser);
  const [firmanteData, setFirmanteData] = useState([]);
  const [historialData, setHistorialData] = useState([]);
  const [tiposDeNominaEmpleado, setTiposDeNominaEmpleado] = useState([]);
  const [open, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [baseURIPagination, setBaseURIPagination] = useState();
  const [selectedEmpleado, setSelectedEmpleado] = useState();

  let baseURI = 'catalogos/dato-operacional-del-empleado-paginado/?empleados_filter=';

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _datoOperacionalesDeEmpleados,
        _empleados,
        _empleadosFirmante,
        _puestos,
        _areas,
        _adminPlazas,
        _plazas,
        _tipos_de_nominas_empleados,
        _firmanteData,
        _historialData,
      ] = await Promise.all([
        getDatoOperacionalesDeEmpleados(),
        getEmpleadosFiltered(),
        getEmpleadosFiltered(),
        getPuestos(),
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getAdministracionDePlazas(),
        getPlazas(),
        getTiposDeNominasEmpleados(),
        getFirmantesComprobantesDeSupervivencia(),
        getHistorialComprobantesDeSupervivencia(),
      ]);
      const formattedURS = normalizeData(_areas);
      if (mounted) {
        // Filtramos la lista de todos los empleados, buscamos en
        // sus datos operacionales, para cumplir la condición de:
        // - Si el empleado coincide con su dato operacional y además ese empleado es JUBILADO ó PENSIONADO.
        const empleadosFiltered = _empleados?.filter((empleado) => _datoOperacionalesDeEmpleados.some((value) => value?.empleado === empleado.id && (value.tipo_de_funcion === TIPO_DE_FUNCION_JUBILADO || value.tipo_de_funcion === TIPO_DE_FUNCION_PENSIONADO)));

        setEmpleados(empleadosFiltered);
        setEmpleadosFirmante(_empleadosFirmante);
        setPuestos(_puestos);
        setAreas(formattedURS);
        setAdminPlazas(_adminPlazas);
        setPlazas(_plazas);
        setTiposDeNominaEmpleado(_tipos_de_nominas_empleados);
        setFirmanteData(_firmanteData);
        setHistorialData(_historialData);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const fetchHistorialData = async () => {
    const _data = await getHistorialComprobantesDeSupervivencia();
    setHistorialData(formatReceived(_data));
  };

  const fetchFirmanteData = async () => {
    const _data = await getFirmantesComprobantesDeSupervivencia();
    setFirmanteData(formatReceived(_data));
  };

  const onPrintAlert = () => {
    setVisibleAlert(true);
  };

  const postFirmante = async () => {
    try {
      const _adminPlaza = adminPlazas?.find((e) => e.empleado === user?.empleado);
      const _plaza = plazas?.find((e) => e.id === _adminPlaza?.plaza);
      const puestoFirmante = puestos?.find((e) => e.id === _plaza?.puesto)?.id;
      const values = {
        usuario: user?.id,
        puesto: puestoFirmante,
        area: _plaza?.area,
        area_content_id: _plaza?.area_content_id,
      };
      await postFirmanteComprobantesDeSupervivencia(values);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const postHistorial = async (idEmpleado, fechaHoraEmision, fechaProximaFirma) => {
    try {
      const values = {
        fecha_hora_emision: fechaHoraEmision,
        fecha_proxima_firma: fechaProximaFirma,
        empleado: idEmpleado,
        usuario: user?.id,
      };
      await postHistorialComprobanteDeSupervivencia(values);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const onPrint = async () => {
    try {
      setLoading(true);
      const fechaHoraEmision = moment().format('YYYY-MM-DDTHH:mm');
      const fechaHoraEmisionPost = moment(fechaHoraEmision).format('YYYY-MM-DD');
      const fechaProximaFirma = moment().add(6, 'months').format('YYYY-MM-DD');
      await postHistorial(selectedEmpleado?.id, fechaHoraEmision, fechaProximaFirma);
      await postFirmante();
      let pdf;
      if (user?.empleado) {
        pdf = await descargarComprobanteSupervivencia(selectedEmpleado?.id, user?.empleado, fechaHoraEmisionPost, fechaProximaFirma);
      } else {
        pdf = await descargarComprobanteSupervivenciaUsuarioNoEmp(selectedEmpleado?.id, user?.id, fechaHoraEmisionPost, fechaProximaFirma);
      }
      const _empleado = empleados?.find((empleado) => empleado?.id === selectedEmpleado?.id);
      const name = namePDF('Comprobante de Supervivencia', _empleado);
      if (pdf) {
        const blob = new Blob([pdf], {
          type: 'application/pdf',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.click();
        await fetchHistorialData();
        await fetchFirmanteData();
      }
      setVisibleAlert(false);
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const onChangeEmpleado = (val) => {
    setLoading(true);
    if (val) {
      const empleado = empleados?.find((emp) => emp?.id === val);
      if (empleado) {
        const _adminPlaza = adminPlazas?.find((e) => e.empleado === empleado?.id);
        const _plaza = plazas?.find((e) => e.id === _adminPlaza?.plaza);
        const areaEmp = areaBuscar(_plaza?.area_content_id, areas)?.title;
        const puestoEmp = puestos?.find((e) => e.id === _plaza?.puesto)?.descripcion;
        form.setFieldsValue({
          rfc: empleado?.RFC,
          area: areaEmp,
          puesto: puestoEmp,
        });
        setAllowPrint(true);
        return setLoading(false);
      }
      form.resetFields(['area', 'puesto', 'rfc']);
      return setLoading(false);
    }
    setAllowPrint(false);
    form.resetFields();
    return setLoading(false);
  };

  const validateInput = async () => {
    setLoading(true);
    setSelectedEmpleado();
    const searchEmp = form.getFieldValue('empleado');
    if (!searchEmp) {
      setVisible(true);
      return setLoading(false);
    }
    const res = await getEmpleadosConDatosOpFilteredPaginated(`${baseURI}numero_de_empleado=${searchEmp};`);
    const result = res?.results[0];
    if (!res) {
      return setLoading(false);
    }
    const empleadoResult = result?.empleado;
    setSelectedEmpleado(empleadoResult);
    form.setFieldsValue({
      empleado: `${empleadoResult?.numero_de_empleado || ''} - ${empleadoResult?.nombre || ''} ${empleadoResult?.apellido_paterno || ''} ${empleadoResult?.apellido_materno || ''}`,
    });
    onChangeEmpleado(empleadoResult?.id);
    return setLoading(false);
  };

  const searchData = async (empId, nombreEmp, apPatEmp, apMatEmp, tipoNominaEmpleado) => {
    setLoading(true);
    if (empId) {
      baseURI = `${baseURI}numero_de_empleado=${empId};`;
    }
    if (nombreEmp) {
      baseURI = `${baseURI}nombre=${nombreEmp};`;
    }
    if (apPatEmp) {
      baseURI = `${baseURI}apellido_paterno=${apPatEmp};`;
    }
    if (apMatEmp) {
      baseURI = `${baseURI}apellido_materno=${apMatEmp};`;
    }
    if (tipoNominaEmpleado) {
      baseURI = `${baseURI}&tipo_de_nomina_empleado=${tipoNominaEmpleado}&`;
    }
    setBaseURIPagination(baseURI);
    const _empleados = await getEmpleadosConDatosOpFilteredPaginated(baseURI);
    setData(formatReceivedClone(_empleados));
    return setLoading(false);
  };

  const columnsEmpleado = [
    {
      titleText: 'No. de Empleado',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 200,
      render: (empObject) => empObject?.numero_de_empleado,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 250,
      render: (empObject) => empObject?.nombre,
    },
    {
      titleText: 'Apellido Paterno',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 250,
      render: (empObject) => empObject?.apellido_paterno,
    },
    {
      titleText: 'Apellido Materno',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 250,
      render: (empObject) => empObject?.apellido_materno,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 300,
      render: (empObject) => {
        const adminPlazaEmp = adminPlazas?.find((admP) => admP?.empleado === empObject?.id);
        const plazaEmp = plazas?.find((pl) => pl?.id === adminPlazaEmp?.plaza);
        const puestoEmp = puestos?.find((p) => p?.id === plazaEmp?.puesto);
        return `${puestoEmp?.descripcion || ''}`;
      },
    },
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 250,
      render: (tneEmp) => tiposDeNominaEmpleado?.find((tne) => tne?.id === tneEmp)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          className="form-container"
          title="Comprobante de Supervivencia"
          extra={(
            <FormSubmitControls
              onPrint={() => onPrintAlert()}
              allowPrint={allowPrint}
            />
          )}
        >
          <Tabs
            type="card"
            activeKey={currentTabKey}
            onChange={(val) => {
              form.resetFields();
              setSelectedEmpleado();
              setAllowPrint(false);
              setCurrentTabKey(val);
            }}
          >
            <TabPane
              tab={(
                <span>
                  Comprobante
                </span>
              )}
              key="comprobante"
              forceRender
            >
              <Form
                layout="vertical"
                form={form}
                // onFinish={onFinish}
                scrollToFirstError
                name="general"
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Row align="middle">
                      <Col xs={24} sm={24} md={24}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Form.Item
                            label="No. y Nombre del Empleado"
                            name="empleado"
                            hasFeedback
                          >
                            <Input
                              allowClear
                            />
                          </Form.Item>
                          <Button
                            loading={loading}
                            onClick={validateInput}
                            icon={<SearchOutlined />}
                            style={{
                              width: '2.7rem',
                              marginTop: '-2px',
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Col>
                  {selectedEmpleado
                    ? (
                      form.setFieldsValue({
                        empleado: `${selectedEmpleado?.numero_de_empleado || ''} - ${selectedEmpleado?.nombre || ''} ${selectedEmpleado?.apellido_paterno || ''} ${selectedEmpleado?.apellido_materno || ''}`,
                      })
                    ) : null}
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="area"
                      label="Área"
                    >
                      <Input
                        allowClear
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="puesto"
                      label="Puesto"
                    >
                      <Input
                        allowClear
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="rfc"
                      label="RFC"
                    >
                      <Input
                        allowClear
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </TabPane>
            <TabPane
              tab={(
                <span>
                  Firmante
                </span>
              )}
              key="firmante"
              forceRender
            >
              <FirmanteComprobanteDeSupervivencia
                firmanteData={firmanteData}
                empleadosFirmante={empleadosFirmate}
              />
            </TabPane>
            <TabPane
              tab={(
                <span>
                  Historial
                </span>
              )}
              key="historial"
              forceRender
            >
              <HistorialComprobanteDeSupervivencia
                historialData={historialData}
                permission={permission}
              />
            </TabPane>
          </Tabs>
        </Card>
        <ModalPrint
          onPrint={() => onPrint()}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`el Comprobante de Supervivencia de: ${`${selectedEmpleado?.nombre || ''} ${selectedEmpleado?.apellido_paterno || ''} ${selectedEmpleado?.apellido_materno || ''}`}`}
          loading={loading}
        />
        <CustomSearch
          form={form}
          open={open}
          setVisible={setVisible}
          modalTitle="Buscar Empleado"
          firstInputLabel="No. de Empleado"
          firstInputName="numero_de_empleado"
          secondInputLabel="Nombre del Empleado"
          secondInputName="nombre"
          searchInputLabel="Apellido Paterno"
          searchInputName="apellido_paterno"
          thirdInputLabel="Apellido Materno"
          thirdInputName="apellido_materno"
          firstSelectLabel="Tipo de Nómina Empleado"
          firstSelectName="tipo_de_nomina_empleado"
          firstSelectDataSource={tiposDeNominaEmpleado}
          inputsNeeded={3}
          selectsNeeded={1}
          paginatedTable
          modalWidth={1400}
          columns={columnsEmpleado}
          baseURI={baseURIPagination}
          setSelectedRow={setSelectedEmpleado}
          onFinishGet={getEmpleadosConDatosOpFilteredPaginated}
          formatReceivedClone={formatReceivedClone}
          data={data}
          setData={setData}
          searchData={searchData}
          onChange={onChangeEmpleado}
        />
      </Spin>
    </Row>
  );
};

export default ConsultaComprobanteDeSupervivencia;
